/**
 * Client mirror of the IAP attachment manifest.
 *
 * The manifest drives two things at once: the checkboxes printed on the IAP
 * cover (the ICS-202 itself, in the FEMA family) and which forms are actually
 * merged into the bundle. Keep in step with
 * services/coms/src/routes/Forms/iapAttachmentManifest.ts.
 */

import {
  isFormImplementedForFamily,
  type IcsFormFamily,
} from 'components/IAP/formFamily';

export const IAP_BASE_FORM_IDS = [
  'ICS_202',
  'ICS_203',
  'ICS_204',
  'ICS_205',
  'ICS_205A',
  'ICS_206',
  'ICS_207',
  'ICS_208',
] as const;

export type IapBaseFormId = (typeof IAP_BASE_FORM_IDS)[number];

/**
 * Extra ICS forms a Coast Guard IAP can merge beyond the cover checkboxes.
 * ICS_204A is omitted: it has no standalone exportAsForm case and rides with 204.
 */
export const IAP_APPENDABLE_FORM_IDS = [
  'ICS_201',
  'ICS_202A',
  'ICS_202B',
  'ICS_208HM',
  'ICS_209',
  'ICS_211',
  'ICS_211A',
  'ICS_213RR',
  'ICS_214',
  'ICS_215',
  'ICS_215A',
  'ICS_217A',
  'ICS_220',
  'ICS_220UAS',
  'ICS_221',
  'ICS_225',
  'ICS_230',
  'ICS_232',
  'ICS_232A',
  'ICS_233',
  'ICS_234',
  'ICS_235',
  'ICS_236',
  'ICS_238',
] as const;

export type IapAppendableFormId = (typeof IAP_APPENDABLE_FORM_IDS)[number];

export const IAP_FORM_IDS = [
  ...IAP_BASE_FORM_IDS,
  ...IAP_APPENDABLE_FORM_IDS,
] as const;

export type IapFormId = (typeof IAP_FORM_IDS)[number];

/** A file manager document the user asked to travel with the plan. */
export type IapFileAttachment = {
  /** `notefolder.id` of a file uploaded at incident or organization scope. */
  fileId: string;
  /** What the cover prints for it, defaulting to the file's own name. */
  label: string;
};

export type IapAttachmentManifest = {
  forms: IapFormId[];
  mapChart: boolean;
  weatherTides: boolean;
  /** Named on the cover but not merged. */
  otherAttachments: string[];
  /** Named on the cover and appended to the end of the bundle. */
  fileAttachments: IapFileAttachment[];
};

export type { IcsFormFamily } from 'components/IAP/formFamily';

export const IAP_FAMILY_CAPABILITIES: Record<
  IcsFormFamily,
  {
    supportedForms: readonly IapBaseFormId[];
    /**
     * How many "Other Attachments" rows the printed form provides. Attached
     * files and free-text labels share them, since both print into those rows.
     */
    otherAttachmentLimit: number;
    /**
     * Forms the user cannot deselect. FEMA prints the attachment checkboxes on
     * the 202 itself, so the 202 is the cover and is always present.
     */
    alwaysIncludedForms: readonly IapBaseFormId[];
  }
> = {
  uscg: {
    supportedForms: IAP_BASE_FORM_IDS,
    otherAttachmentLimit: 10,
    alwaysIncludedForms: [],
  },
  fema: {
    supportedForms: ['ICS_202', 'ICS_204', 'ICS_207', 'ICS_208'],
    otherAttachmentLimit: 4,
    alwaysIncludedForms: ['ICS_202'],
  },
};

export const IAP_FORM_LABELS: Record<IapBaseFormId, string> = {
  ICS_202: 'ICS 202 - Incident Objectives',
  ICS_203: 'ICS 203 - Organization Assignment List',
  ICS_204: 'ICS 204 - Assignment List',
  ICS_205: 'ICS 205 - Communications Plan',
  ICS_205A: 'ICS 205A - Communications List',
  ICS_206: 'ICS 206 - Medical Plan',
  ICS_207: 'ICS 207 - Organization Chart',
  ICS_208: 'ICS 208 - Safety Message',
};

export const IAP_APPENDABLE_FORM_LABELS: Record<IapAppendableFormId, string> = {
  ICS_201: 'ICS 201 - Incident Briefing',
  ICS_202A: 'ICS 202A - Command Direction',
  ICS_202B: 'ICS 202B - Critical Information Requirements',
  ICS_208HM: 'ICS 208HM - Site Safety and Control Plan',
  ICS_209: 'ICS 209 - Incident Status Summary',
  ICS_211: 'ICS 211 - Check-In List',
  ICS_211A: 'ICS 211A - Check-In List (Continuation)',
  ICS_213RR: 'ICS 213RR - Resource Request',
  ICS_214: 'ICS 214 - Activity Log',
  ICS_215: 'ICS 215 - Operational Planning Worksheet',
  ICS_215A: 'ICS 215A - Incident Action Plan Safety Analysis',
  ICS_217A: 'ICS 217A - Communications Resource Availability',
  ICS_220: 'ICS 220 - Air Operations Summary',
  ICS_220UAS: 'ICS 220UAS - UAS Operations Summary',
  ICS_221: 'ICS 221 - Demobilization Check-Out',
  ICS_225: 'ICS 225 - Incident Personnel Performance Rating',
  ICS_230: 'ICS 230 - Daily Meeting Schedule',
  ICS_232: 'ICS 232 - Resources at Risk Summary',
  ICS_232A: 'ICS 232A - Resources at Risk Summary (Continuation)',
  ICS_233: 'ICS 233 - Incident Open Action Tracker',
  ICS_234: 'ICS 234 - Work Analysis Matrix',
  ICS_235: 'ICS 235 - Facility Needs Assessment',
  ICS_236: 'ICS 236 - Tentative Release List',
  ICS_238: 'ICS 238 - Incident Staffing Summary',
};

export function iapFormLabel(formId: IapFormId): string {
  if ((IAP_BASE_FORM_IDS as readonly string[]).includes(formId)) {
    return IAP_FORM_LABELS[formId as IapBaseFormId];
  }
  return IAP_APPENDABLE_FORM_LABELS[formId as IapAppendableFormId];
}

/** Short display name, e.g. "ICS 205A". */
export function formatIapFormName(formId: IapFormId): string {
  return formId.replace('_', ' ');
}

function isSelectableIapForm(id: string, family: IcsFormFamily): id is IapFormId {
  if ((IAP_BASE_FORM_IDS as readonly string[]).includes(id)) {
    return IAP_FAMILY_CAPABILITIES[family].supportedForms.includes(
      id as IapBaseFormId
    );
  }
  return (
    (IAP_APPENDABLE_FORM_IDS as readonly string[]).includes(id) &&
    isFormImplementedForFamily(id, family)
  );
}

export function defaultIapAttachmentManifest(
  family: IcsFormFamily
): IapAttachmentManifest {
  return {
    forms: [...IAP_FAMILY_CAPABILITIES[family].supportedForms],
    mapChart: false,
    weatherTides: false,
    otherAttachments: [],
    fileAttachments: [],
  };
}

/** Unset means never configured, which resolves to the family default. */
export function resolveIapAttachmentManifest(
  stored: Partial<IapAttachmentManifest> | null | undefined,
  family: IcsFormFamily
): IapAttachmentManifest {
  if (!stored) return defaultIapAttachmentManifest(family);

  const capability = IAP_FAMILY_CAPABILITIES[family];

  // Attached files claim the "Other Attachments" rows ahead of the free-text
  // labels: a merged document that went unnamed on the cover would leave the
  // reader no way to tell what the extra pages are.
  const fileAttachments = dedupeFileAttachments(
    stored.fileAttachments ?? []
  ).slice(0, capability.otherAttachmentLimit);

  return {
    forms: orderIapForms([
      ...(stored.forms ?? []).filter((id) => isSelectableIapForm(id, family)),
      ...capability.alwaysIncludedForms,
    ]),
    mapChart: false,
    weatherTides: false,
    otherAttachments: (stored.otherAttachments ?? []).slice(
      0,
      capability.otherAttachmentLimit - fileAttachments.length
    ),
    fileAttachments,
  };
}

/** One row per file, so picking the same document twice does not merge it twice. */
function dedupeFileAttachments(
  attachments: readonly IapFileAttachment[]
): IapFileAttachment[] {
  const seen = new Set<string>();
  return attachments.filter((attachment) => {
    if (seen.has(attachment.fileId)) return false;
    seen.add(attachment.fileId);
    return true;
  });
}

/** Canonical ICS numbering, so 205A always trails 205 regardless of click order. */
export function orderIapForms(formIds: readonly IapFormId[]): IapFormId[] {
  const selected = new Set(formIds);
  return IAP_FORM_IDS.filter((id) => selected.has(id));
}

export function areManifestsEqual(
  a: IapAttachmentManifest,
  b: IapAttachmentManifest
): boolean {
  return (
    a.mapChart === b.mapChart &&
    a.weatherTides === b.weatherTides &&
    a.forms.length === b.forms.length &&
    a.forms.every((id, index) => id === b.forms[index]) &&
    a.otherAttachments.length === b.otherAttachments.length &&
    a.otherAttachments.every(
      (label, index) => label === b.otherAttachments[index]
    ) &&
    a.fileAttachments.length === b.fileAttachments.length &&
    a.fileAttachments.every(
      (attachment, index) =>
        attachment.fileId === b.fileAttachments[index]?.fileId &&
        attachment.label === b.fileAttachments[index]?.label
    )
  );
}
